import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/card";
import { LoadingSpinner } from "@/components/loading-spinner";
import { useTeamGoalsCPA, useTeamGoalsCPAClients, useTeamGoalsSummary } from "@/http/api-hooks";
import { CPAOrgData } from "@/http/api-types";
import { subMonths } from "date-fns";
import { useEffect, useState } from "react";
import { CPAChart } from "./cpa-chart";
import { ExperimentsChart } from "./experiments-chart";
import { TasksChart } from "./tasks-chart";

const EXPERIMENTS_GOAL = 5;
// 3 working days in business seconds
const TASKS_GOAL_SECONDS = 259200;

function getLastWeekStart(): string {
  const now = new Date();
  const dayOfWeek = now.getUTCDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(now);
  monday.setUTCDate(monday.getUTCDate() - daysToMonday - 7);
  monday.setUTCHours(0, 0, 0, 0);
  return monday.toISOString().slice(0, 10);
}

const CPA_GOAL = 1.0;

function getAchievingCPACount(orgs: CPAOrgData[]): { achieving: number; total: number } {
  let achieving = 0;
  for (const org of orgs) {
    const week4 = org.weeks.find(w => w.week_number === 4);
    if (week4 && week4.cpa_achievement > 0 && week4.cpa_achievement < CPA_GOAL) {
      achieving++;
    }
  }
  return { achieving, total: orgs.length };
}

function GoalStatusBadge({ met }: { met: boolean }) {
  return (
    <span className={met ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
      {met ? "On target" : "Below target"}
    </span>
  );
}

export default function TeamGoalsPage() {
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);
  const [initialized, setInitialized] = useState(false);

  const { data: summaryData, isLoading: summaryLoading } = useTeamGoalsSummary();
  const { data: clientsData, isLoading: clientsLoading } = useTeamGoalsCPAClients();
  const { data: cpaData, isLoading: cpaLoading } = useTeamGoalsCPA(selectedOrgIds, selectedOrgIds.length > 0);

  useEffect(() => {
    if (!initialized && clientsData?.data) {
      const sixMonthsAgo = subMonths(new Date(), 6);
      const recentIds = clientsData.data
        .filter(c => new Date(c.first_campaign_date) >= sixMonthsAgo)
        .map(c => c.org_id);
      setSelectedOrgIds(recentIds);
      setInitialized(true);
    }
  }, [clientsData, initialized]);

  const experiments = summaryData?.experiments ?? [];
  const tasks = summaryData?.tasks ?? [];
  const orgs = cpaData?.data ?? [];
  const allClients = clientsData?.data ?? [];

  const cpaStats = getAchievingCPACount(orgs);

  const lastWeekStart = getLastWeekStart();
  const expCount = summaryLoading
    ? null
    : (experiments.find(e => e.week_start_utc === lastWeekStart)?.concluded_count ?? 0);
  const lastWeekTask = tasks.find(t => t.week_start_utc === lastWeekStart);
  const taskDays = lastWeekTask ? parseFloat((lastWeekTask.median_cycle_seconds / 86400).toFixed(1)) : null;

  return (
    <>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Goal 1 – CPA Achievement</CardTitle>
          </CardHeader>
          <CardContent>
            {cpaLoading ? (
              <LoadingSpinner />
            ) : orgs.length > 0 ? (
              <>
                <div className="text-2xl font-bold">
                  {cpaStats.achieving} / {cpaStats.total}
                </div>
                <p className="text-xs text-muted-foreground">clients achieving CPA by week 4</p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Select clients to see CPA status</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Goal 2 – Experiments Per Week</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <LoadingSpinner />
            ) : expCount !== null ? (
              <>
                <div className="text-2xl font-bold">{expCount}</div>
                <p className="text-xs text-muted-foreground">
                  concluded last week · <GoalStatusBadge met={expCount >= EXPERIMENTS_GOAL} />
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No data</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Goal 3 – Task Cycle Time</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <LoadingSpinner />
            ) : taskDays !== null ? (
              <>
                <div className="text-2xl font-bold">{taskDays}d</div>
                <p className="text-xs text-muted-foreground">
                  median last week · <GoalStatusBadge met={lastWeekTask!.median_cycle_seconds <= TASKS_GOAL_SECONDS} />
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No data</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Goal 1 – CPA Achievement per Client</CardTitle>
            <CardDescription>
              Weekly CPA achievement (revenue / CPA target) by weeks since campaign start. 100% and below means on
              target.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CPAChart
              orgs={orgs}
              allClients={allClients}
              selectedOrgIds={selectedOrgIds}
              onSelectionChange={setSelectedOrgIds}
              isLoadingClients={clientsLoading}
              isLoadingData={cpaLoading}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Goal 2 – Concluded Experiments Per Week</CardTitle>
            <CardDescription>
              Online experiments with a trusted insight (conclusive outcome) per week. Goal: 5 per week.
            </CardDescription>
          </CardHeader>
          <CardContent>{summaryLoading ? <LoadingSpinner /> : <ExperimentsChart data={experiments} />}</CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Goal 3 – Non-Experiment Task Cycle Time (Median)</CardTitle>
            <CardDescription>
              Median cycle time in working days for non-experiment tickets completed each week. Goal: 3 working days.
            </CardDescription>
          </CardHeader>
          <CardContent>{summaryLoading ? <LoadingSpinner /> : <TasksChart data={tasks} />}</CardContent>
        </Card>
      </div>
    </>
  );
}
